<template>
  <div class="checkout-root" :class="{ 'checkout-root--light': !$vuetify.theme.dark }">

    <!-- Top bar -->
    <div class="checkout-topbar">
      <img :src="require('@/assets/images/zapermenu.png')" height="34" alt="ZaperMenu" />
      <div class="topbar-plan" v-if="selectedPlan">
        <v-icon small color="success" class="mr-1">mdi-shield-check</v-icon>
        {{ selectedPlan.label }} — R${{ selectedPlan.price }}/mês
      </div>
    </div>

    <!-- Card central -->
    <div class="checkout-center">
      <transition :name="transitionName" mode="out-in">

        <!-- Tela de boas-vindas -->
        <div v-if="step === 0" key="welcome" class="quiz-card">

          <!-- Lead retornando com dados salvos -->
          <template v-if="hasPartialAnswers">
            <div class="resume-badge">
              <v-icon small color="success" class="mr-1">mdi-content-save-check</v-icon>
              Cadastro salvo
            </div>
            <h2 class="quiz-card__title mt-3">
              Bem-vindo de volta{{ quizAnswers.nome ? ', ' + quizAnswers.nome.split(' ')[0] : '' }}!
            </h2>
            <p class="quiz-card__desc">
              Encontramos seu progresso salvo. Você pode continuar de onde parou.
            </p>
            <button class="btn-primary mt-5" @click="start">Continuar de onde parei →</button>
            <button class="btn-restart mt-3" @click="clearAndStart">
              <v-icon small class="mr-1">mdi-restart</v-icon> Recomeçar do início
            </button>
          </template>

          <!-- Novo lead -->
          <template v-else>
            <div class="quiz-icon">🚀</div>
            <h2 class="quiz-card__title">Vamos criar sua conta!</h2>
            <p class="quiz-card__desc">
              São apenas <strong>algumas perguntas rápidas</strong> para personalizar sua experiência.<br>
              Leva menos de 1 minuto.
            </p>
            <div class="plan-summary" v-if="selectedPlan">
              <div class="plan-summary__row">
                <span>Plano selecionado</span>
                <strong>{{ selectedPlan.label }}</strong>
              </div>
              <div class="plan-summary__row">
                <span>Valor</span>
                <strong>R${{ selectedPlan.price }}/mês</strong>
              </div>
              <div class="plan-summary__row green-row">
                <v-icon small color="success" class="mr-1">mdi-shield-check</v-icon>
                <span>Garantia de 30 dias ou seu dinheiro de volta</span>
              </div>
            </div>
            <button class="btn-primary mt-6" @click="start">Começar →</button>
          </template>

        </div>

        <!-- Perguntas -->
        <div v-else-if="step <= questions.length" :key="currentQuestion.id" class="quiz-card">
          <div class="quiz-progress">
            <div
              class="quiz-progress__bar"
              :style="{ width: (step / questions.length * 100) + '%' }"
            />
          </div>
          <p class="quiz-step-label">{{ step }} de {{ questions.length }}</p>

          <h3 class="quiz-card__title">{{ currentQuestion.title }}</h3>
          <p v-if="currentQuestion.desc" class="quiz-card__desc">{{ currentQuestion.desc }}</p>

          <!-- Single select -->
          <div v-if="currentQuestion.type === 'single'" class="options-grid">
            <button
              v-for="opt in currentQuestion.options"
              :key="opt.value"
              class="option-btn"
              :class="{ 'option-btn--selected': answers[currentQuestion.id] === opt.value }"
              @click="selectSingle(opt.value)"
            >
              <i v-if="opt.icon" :class="'mdi mdi-' + opt.icon + ' option-icon'"></i>
              {{ opt.label }}
            </button>
          </div>

          <!-- Multiple select -->
          <div v-else-if="currentQuestion.type === 'multiple'" class="options-grid">
            <button
              v-for="opt in currentQuestion.options"
              :key="opt.value"
              class="option-btn"
              :class="{ 'option-btn--selected': (answers[currentQuestion.id] || []).includes(opt.value) }"
              @click="toggleMultiple(currentQuestion.id, opt.value)"
            >
              <i v-if="opt.icon" :class="'mdi mdi-' + opt.icon + ' option-icon'"></i>
              {{ opt.label }}
            </button>
          </div>

          <!-- Text input -->
          <div v-else-if="currentQuestion.type === 'text'" class="text-input-wrap">
            <input
              v-focus
              v-model="answers[currentQuestion.id]"
              class="quiz-input"
              :placeholder="currentQuestion.placeholder"
              :type="currentQuestion.inputType || 'text'"
              @keydown.enter="isAnswered && nextStep()"
            />
          </div>

          <!-- Tel input -->
          <div v-else-if="currentQuestion.type === 'tel'" class="text-input-wrap">
            <input
              v-focus
              v-model="answers[currentQuestion.id]"
              v-mask="['(##) ####-####', '(##) #####-####']"
              class="quiz-input"
              placeholder="(99) 99999-9999"
              type="tel"
              @keydown.enter="isAnswered && nextStep()"
            />
          </div>

          <div class="quiz-actions mt-6">
            <button v-if="step > 1" class="btn-back" @click="prevStep">← Voltar</button>
            <button
              class="btn-primary"
              :disabled="!isAnswered"
              @click="nextStep"
            >
              {{ step === questions.length ? 'Finalizar →' : 'Próxima →' }}
            </button>
          </div>
        </div>

        <!-- Loading final -->
        <div v-else key="loading" class="quiz-card quiz-card--center">
          <div class="loading-spinner"></div>
          <h3 class="mt-4">Salvando seus dados...</h3>
          <p class="quiz-card__desc">Aguarde um instante</p>
        </div>

      </transition>
    </div>

    <!-- Selos de confiança -->
    <div class="trust-bar">
      <span><v-icon small color="success">mdi-lock</v-icon> Dados protegidos</span>
      <span><v-icon small color="success">mdi-headset</v-icon> Suporte humanizado</span>
      <span><v-icon small color="success">mdi-shield-check</v-icon> Garantia 30 dias</span>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex';
import { trackPlan, eventIdLead } from '@/utils/track';
import { cookiesMeta } from '@/utils/attribution';
import { uuidDaResposta } from '@/utils/api';
import {
  trackClick,
  trackQuizInicio,
  trackQuizResposta,
  trackQuizCompleto,
  trackLeadCriado,
} from '@/utils/lpTracker';

export default {
  name: 'Checkout',

  data: () => ({
    step: 0,
    direction: 'next',
    answers: {},

    questions: [
      {
        id: 'nome',
        title: 'Qual é o seu nome?',
        type: 'text',
        placeholder: 'Digite seu nome completo',
      },
      {
        id: 'whatsapp',
        title: 'Qual o seu WhatsApp?',
        desc: 'Informe seu whatsapp pessoal ou comercial para contato.',
        type: 'tel',
        placeholder: '(99) 99999-9999',
      },
      {
        id: 'ramo',
        title: 'Qual é o seu ramo?',
        desc: 'Selecione o tipo de estabelecimento.',
        type: 'single',
        options: [
          { value: 'HAMBURGUERIA',  label: 'Hamburgueria',  icon: 'food' },
          { value: 'PIZZARIA',      label: 'Pizzaria',      icon: 'food-variant' },
          { value: 'RESTAURANTE',   label: 'Restaurante',   icon: 'silverware-fork-knife' },
          { value: 'ACAITERIA',     label: 'Açaiteria',     icon: 'leaf' },
          { value: 'LANCHONETE',    label: 'Lanchonete',    icon: 'bread-slice' },
          { value: 'MARMITARIA',    label: 'Marmitaria',    icon: 'food-variant' },
          { value: 'SALGADERIA',    label: 'Salgaderia',    icon: 'food-croissant' },
          { value: 'CAFETERIA',     label: 'Cafeteria',     icon: 'coffee' },
          { value: 'PADARIA',       label: 'Padaria',       icon: 'bread-slice-outline' },
          { value: 'DOCERIA',       label: 'Doceria',       icon: 'cake-variant' },
          { value: 'OUTROS',        label: 'Outros',        icon: 'store' },
        ],
      },
      {
        id: 'pedidos',
        title: 'Quantos pedidos recebe por dia?',
        type: 'single',
        options: [
          { value: 'DEZ',          label: '1 a 10 pedidos',    icon: 'package-variant-closed' },
          { value: 'VINTE',        label: '11 a 20 pedidos',   icon: 'package-variant-closed' },
          { value: 'TRINTA',       label: '21 a 30 pedidos',   icon: 'package-variant-closed' },
          { value: 'TRINTA_MAIS',  label: 'Mais de 30',        icon: 'fire' },
          { value: 'INAUGURACAO',  label: 'Ainda vou abrir',   icon: 'flag-outline' },
        ],
      },
      {
        id: 'faturamento',
        title: 'Qual seu faturamento mensal aproximado?',
        desc: 'Isso nos ajuda a entender melhor como te atender.',
        type: 'single',
        options: [
          { value: 'ATE_10K',      label: 'Até R$10.000',      icon: 'cash' },
          { value: 'DE_10K_A_20K', label: 'De R$10k a R$20k',  icon: 'cash-multiple' },
          { value: 'DE_20K_A_40K', label: 'De R$20k a R$40k',  icon: 'cash-multiple' },
          { value: 'ACIMA_40K',    label: 'Acima de R$40.000', icon: 'fire' },
        ],
      },
      {
        id: 'atendimento',
        title: 'Como você atende seus clientes?',
        desc: 'Selecione tudo que se aplica.',
        type: 'multiple',
        options: [
          { value: 'DELIVERY', label: 'Delivery',           icon: 'moped' },
          { value: 'BALCAO',   label: 'Retirada no balcão', icon: 'store-outline' },
          { value: 'MESA',     label: 'Consumo no local',   icon: 'silverware-fork-knife' },
        ],
      },
      {
        id: 'impressora',
        title: 'Você tem impressora térmica?',
        desc: 'Para imprimir pedidos automaticamente na cozinha.',
        type: 'single',
        options: [
          { value: 'SIM',        label: 'Sim, já tenho',              icon: 'printer-check' },
          { value: 'NAO',        label: 'Não tenho ainda',            icon: 'printer-off' },
          { value: 'AGUARDANDO', label: 'Comprei, aguardando chegar', icon: 'package-variant-closed' },
        ],
      },
    ],
  }),

  computed: {
    ...mapState(['selectedPlan', 'urlParams', 'contatoUuid', 'quizAnswers']),

    currentQuestion() {
      return this.questions[this.step - 1];
    },

    transitionName() {
      return this.direction === 'next' ? 'slide-left' : 'slide-right';
    },

    isAnswered() {
      if (!this.currentQuestion) return false;
      const val = this.answers[this.currentQuestion.id];
      if (this.currentQuestion.type === 'tel') {
        return !!val && val.replace(/\D/g, '').length >= 10;
      }
      if (this.currentQuestion.type === 'multiple') {
        return Array.isArray(val) && val.length > 0;
      }
      return !!val && String(val).trim().length > 0;
    },

    hasPartialAnswers() {
      if (!this.quizAnswers) return false;
      const keys = ['nome', 'whatsapp', 'ramo', 'pedidos', 'faturamento', 'atendimento', 'impressora'];
      return keys.some(k => {
        const v = this.quizAnswers[k];
        return Array.isArray(v) ? v.length > 0 : !!v;
      });
    },
  },

  directives: {
    focus: {
      inserted(el) {
        el.focus();
      },
    },
  },

  mounted() {
    if (!this.selectedPlan) {
      this.$router.replace('/');
      return;
    }

    // Lead já completou o pré-cadastro → pula direto para o preview
    if (this.contatoUuid) {
      this.$router.replace('/preview');
      return;
    }

    // Restaura respostas parciais salvas no localStorage
    if (this.hasPartialAnswers) {
      this.answers = { ...this.quizAnswers };
    }

    // Tracking — lead entrou no checkout
    trackPlan('InitiateCheckout', 'begin_checkout', this.selectedPlan);
    trackClick('Checkout - Acessou a página', { plano: this.selectedPlan?.label });
  },

  methods: {
    start() {
      this.direction = 'next';
      this.step = this.hasPartialAnswers ? this.findFirstUnansweredStep() : 1;
      trackQuizInicio('checkout');
    },

    // Limpa os dados salvos e recomeça do zero
    clearAndStart() {
      this.$store.commit('clearLead');
      this.answers = {};
      this.direction = 'next';
      this.step = 1;
    },

    // Retorna o índice (1-based) da primeira pergunta ainda não respondida
    findFirstUnansweredStep() {
      for (let i = 0; i < this.questions.length; i++) {
        const q   = this.questions[i];
        const val = this.answers[q.id];
        let answered;
        if (q.type === 'multiple')    answered = Array.isArray(val) && val.length > 0;
        else if (q.type === 'tel')    answered = !!val && val.replace(/\D/g, '').length >= 10;
        else                          answered = !!val && String(val).trim().length > 0;
        if (!answered) return i + 1;
      }
      return 1; // todas respondidas — volta ao início (edge-case)
    },

    selectSingle(value) {
      this.$set(this.answers, this.currentQuestion.id, value);
      this.$nextTick(() => setTimeout(() => this.nextStep(), 220));
    },

    toggleMultiple(id, value) {
      const current = this.answers[id] || [];
      const idx = current.indexOf(value);
      this.$set(this.answers, id, idx === -1
        ? [...current, value]
        : current.filter(v => v !== value));
    },

    nextStep() {
      if (!this.isAnswered) return;

      // Persiste a resposta atual imediatamente no store → localStorage
      this.$store.commit('mergeQuizAnswers', {
        [this.currentQuestion.id]: this.answers[this.currentQuestion.id],
      });

      trackQuizResposta(
        'checkout',
        this.step,
        this.currentQuestion.title,
        this.answers[this.currentQuestion.id],
      );

      if (this.step >= this.questions.length) {
        this.finish();
        return;
      }
      this.direction = 'next';
      this.step++;
    },

    prevStep() {
      if (this.step <= 1) { this.step = 0; return; }
      this.direction = 'prev';
      this.step--;
    },

    async finish() {
      this.direction = 'next';
      this.step = this.questions.length + 1; // loading screen

      const metaCookies = cookiesMeta();

      const payload = {
        nome: this.answers.nome,
        whatsapp: '55' + this.answers.whatsapp.replace(/\D/g, ''),
        ramo: this.answers.ramo,
        pedidos: this.answers.pedidos,
        faturamento: this.answers.faturamento,
        atendimento: this.answers.atendimento,
        impressora: this.answers.impressora,
        anuncio_uuid: this.urlParams.ad,
        origem: this.urlParams.origem || this.urlParams.utm || 'landingpage',
        indicacao_empresa_id: this.urlParams.ref || null,
        plano: this.selectedPlan?.id,

        // Procedência da atribuição — ver [src/utils/attribution.js].
        origem_fonte: this.urlParams.fonte || null,
        origem_primeira: this.urlParams.primeiro ? this.urlParams.primeiro.origem : null,
        anuncio_uuid_primeiro: this.urlParams.primeiro ? this.urlParams.primeiro.ad : null,
        visitante_uuid: this.urlParams.visitante_uuid || null,
        utm_params: this.urlParams.utm_params || null,

        // Conversions API da Meta: cookies do pixel e a página do cadastro.
        meta_fbp: metaCookies.fbp,
        meta_fbc: metaCookies.fbc,
        meta_event_source_url: window.location.href,
      };

      this.$store.commit('mergeQuizAnswers', this.answers);

      try {
        const res = await this.$http.post('contato', payload);

        // Leitura única em [src/utils/api.js]. A expressão anterior nunca acertava:
        // sem o uuid, nem `setContatoUuid` nem os eventos de conversão rodavam.
        const uuid = uuidDaResposta(res);

        if (!uuid) {
          console.warn('[checkout] POST /contato sem uuid na resposta', res && res.data);
        }

        if (uuid) {
          this.$store.commit('setContatoUuid', uuid);
          trackLeadCriado(uuid);
          trackQuizCompleto('checkout', uuid);
        }
        // Com o uuid, o Lead leva o mesmo event_id do servidor (deduplicação da Meta).
        trackPlan('Lead', 'generate_lead', this.selectedPlan, {}, uuid ? { eventID: eventIdLead(uuid) } : null);
      } catch (e) {
        // Segue o fluxo mesmo sem salvar — não bloqueia o usuário
        console.warn('Erro ao salvar contato:', e);
      }

      this.$router.push('/preview');
    },
  },
};
</script>

<style scoped lang="scss">
.checkout-root {
  min-height: 100vh;
  background: var(--page-bg, #0a0a0a);
  display: flex;
  flex-direction: column;
}

/* ── Top bar ── */
.checkout-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 5%;
  background: #000;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.topbar-plan {
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
}

/* ── Center area ── */
.checkout-center {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

/* ── Quiz card ── */
.quiz-card {
  background: #111;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 24px;
  padding: 40px;
  width: 100%;
  max-width: 520px;
  color: #fff;

  @media (max-width: 600px) {
    padding: 28px 20px;
    border-radius: 16px;
  }

  &--center {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 60px 40px;
  }
}

.quiz-icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.resume-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(35, 207, 101, 0.12);
  border: 1px solid rgba(35, 207, 101, 0.3);
  color: #23cf65;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 20px;
  letter-spacing: 0.03em;
}

.btn-restart {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px;
  background: transparent;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: rgba(255, 255, 255, 0.7); }
}

.quiz-card__title {
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
  margin-bottom: 10px;
}

.quiz-card__desc {
  font-size: 15px;
  color: rgba(255,255,255,0.6);
  line-height: 1.6;
  margin: 0;
}

/* ── Plan summary ── */
.plan-summary {
  margin-top: 24px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px;
  overflow: hidden;
}

.plan-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  font-size: 14px;
  color: rgba(255,255,255,0.7);
  border-bottom: 1px solid rgba(255,255,255,0.06);

  &:last-child { border-bottom: none; }

  strong { color: #fff; }
}

.green-row {
  justify-content: flex-start;
  color: #23cf65;
  font-size: 13px;
  gap: 6px;
}

/* ── Progress bar ── */
.quiz-progress {
  height: 4px;
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
  margin-bottom: 10px;
  overflow: hidden;
}

.quiz-progress__bar {
  height: 100%;
  background: #f40612;
  border-radius: 2px;
  transition: width 0.35s ease;
}

.quiz-step-label {
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  margin-bottom: 20px;
}

/* ── Options grid ── */
.options-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-top: 16px;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.04);
  color: rgba(255,255,255,0.8);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, background 0.2s, color 0.2s;

  &:hover {
    border-color: rgba(255,255,255,0.35);
    background: rgba(255,255,255,0.08);
  }

  &--selected {
    border-color: #f40612;
    background: rgba(244,6,18,0.12);
    color: #fff;
  }
}

.option-icon {
  font-size: 17px;
  flex-shrink: 0;
  opacity: 0.8;
}

/* ── Text input ── */
.text-input-wrap {
  margin-top: 16px;
}

.quiz-input {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.05);
  color: #fff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s;

  &::placeholder { color: rgba(255,255,255,0.3); }
  &:focus { border-color: rgba(255,255,255,0.5); }
}

/* ── Actions ── */
.quiz-actions {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-end;
}

.btn-primary {
  padding: 13px 28px;
  border-radius: 12px;
  background: #f40612;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;

  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.35; cursor: not-allowed; }
}

.btn-back {
  padding: 13px 20px;
  border-radius: 12px;
  background: transparent;
  color: rgba(255,255,255,0.5);
  font-size: 14px;
  border: 1px solid rgba(255,255,255,0.12);
  cursor: pointer;
  transition: color 0.2s;
  &:hover { color: rgba(255,255,255,0.8); }
}

/* ── Loading spinner ── */
.loading-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid rgba(255,255,255,0.1);
  border-top-color: #f40612;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Trust bar ── */
.trust-bar {
  display: flex;
  justify-content: center;
  gap: 28px;
  padding: 16px 5%;
  font-size: 12px;
  color: rgba(255,255,255,0.4);
  border-top: 1px solid rgba(255,255,255,0.06);
  flex-wrap: wrap;

  span {
    display: flex;
    align-items: center;
    gap: 5px;
  }
}

/* ── Transitions ── */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.22s ease;
}
.slide-left-enter { opacity: 0; transform: translateX(32px); }
.slide-left-leave-to { opacity: 0; transform: translateX(-32px); }
.slide-right-enter { opacity: 0; transform: translateX(-32px); }
.slide-right-leave-to { opacity: 0; transform: translateX(32px); }

.mt-4 { margin-top: 16px; }
.mt-6 { margin-top: 24px; }

/* ══════════════════════════════════════
   LIGHT THEME OVERRIDES
══════════════════════════════════════ */
.checkout-root--light {
  background: #f0f2f5;

  /* topbar mantém sempre dark para contraste com a logomarca */

  .quiz-card {
    background: #fff;
    border-color: #e5e7eb;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    color: #111827;
  }

  .quiz-card__title { color: #111827; }

  .quiz-card__desc {
    color: #4b5563;
    strong { color: #111827; }
  }

  .plan-summary {
    background: #f9fafb;
    border-color: #e5e7eb;
  }

  .plan-summary__row {
    color: #4b5563;
    border-bottom-color: #f3f4f6;
    strong { color: #111827; }
  }

  .quiz-progress { background: #e5e7eb; }

  .quiz-step-label { color: #9ca3af; }

  .option-btn {
    border-color: #e5e7eb;
    background: #f9fafb;
    color: #374151;

    &:hover { border-color: #9ca3af; background: #f3f4f6; }
    &.option-btn--selected {
      border-color: #f40612;
      background: rgba(244,6,18,0.07);
      color: #111827;
    }
  }

  .quiz-input {
    border-color: #d1d5db;
    background: #f9fafb;
    color: #111827;

    &::placeholder { color: #9ca3af; }
    &:focus { border-color: #6b7280; }
  }

  .btn-back {
    color: #6b7280;
    border-color: #d1d5db;
    &:hover { color: #111827; }
  }

  .btn-restart {
    color: #9ca3af;
    &:hover { color: #374151; }
  }

  .loading-spinner {
    border-color: #e5e7eb;
    border-top-color: #f40612;
  }

  .trust-bar {
    color: #6b7280;
    border-top-color: #e5e7eb;
  }

  h3 { color: #111827; }
}
</style>
